import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import AccordionSidebar from "./AccordionSidebar";
import Header from "./Header";
import submenu from "../layouts/submenuConfig";
import { logout } from "../utils/auth";
import MobileMainLayout from "./mobile/MobileMainLayout";

import { getLatestVersion } from "../api/system/appVersions";
import DesktopUnreadReminder from "../modules/settings/notifications/DesktopUnreadReminder";
import AdminMandatoryNotifications from "../modules/settings/notifications/AdminMandatoryNotifications";
import MaintenanceLockOverlay from "../modules/settings/notifications/MaintenanceLockOverlay";


export default function MainLayout({ children }) {
  const [group, setGroup] = useState("");
  const [sub, setSub] = useState("");
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [appVersion, setAppVersion] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  /* =========================================================
   🧩 Load thông tin user từ localStorage
  ========================================================= */
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      console.warn("⚠️ Không tìm thấy user trong localStorage!");
    }
  }, []);

  /* =========================================================
   📱 Theo dõi kích thước để phân biệt mobile/desktop
  ========================================================= */
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* =========================================================
   🧭 Theo dõi đường dẫn để điều hướng sidebar
  ========================================================= */
  useEffect(() => {
    const path = location.pathname;
    const isMobilePage = ["/home", "/favorites", "/settings"].some((p) =>
      path.startsWith(p)
    );
    if (isMobilePage || path === "/login") return;

    const [, urlGroup, urlSub] = path.split("/");

    if (urlGroup && urlSub) {
      setGroup(urlGroup);
      setSub(urlSub);
    } else {
      const storedModules = localStorage.getItem("modules");
      const userModules = storedModules ? JSON.parse(storedModules) : [];

      if (userModules.length > 0) {
        // Ưu tiên HRM Tasks nếu có quyền
        const canAccessHRMTasks = userModules.some(
          (m) =>
            m.id === "hrm" &&
            m.permissions?.some(
              (p) => p.code === "hrm.tasks" && p.actions?.includes("R")
            )
        );

        if (canAccessHRMTasks) {
          setGroup("hrm");
          setSub("tasks");
          navigate("/hrm/tasks");
        } else {
          // Mặc định mở module đầu tiên
          const defaultGroup = userModules[0].id;
          const defaultSub = submenu[defaultGroup]?.[0]?.key || "dashboard";
          setGroup(defaultGroup);
          setSub(defaultSub);
          navigate(`/${defaultGroup}/${defaultSub}`);
        }
      }
    }
  }, [location.pathname, navigate]);

  /* =========================================================
   🧩 Chọn chức năng từ sidebar accordion (chọn cả module + chức năng)
  ========================================================= */
  const handleAccordionSelect = (newGroup, key) => {
    if (!newGroup || !key) return;
    setGroup(newGroup);
    setSub(key);
    navigate(`/${newGroup}/${key}`);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // =======================================================
  // 🔹 Load phiên bản ứng dụng
  // =======================================================
  useEffect(() => {
    async function loadVersion() {
      try {
        const latest = await getLatestVersion();
        if (latest) setAppVersion(latest.version_code); 
      } catch (err) {
        console.error("Không thể tải version:", err);
      }
    }
    loadVersion();
  }, []);
  

  /* =========================================================
   📱 Render cho mobile
  ========================================================= */
  if (isMobile) {
    return <MobileMainLayout user={user}>{children}</MobileMainLayout>;
  }

  /* =========================================================
   🖥 Render cho desktop
  ========================================================= */
  return (
    <div className="flex h-screen w-screen overflow-hidden">
      <AccordionSidebar
        activeGroup={group}
        activeSub={sub}
        onSelect={handleAccordionSelect}
        onLogout={handleLogout}
        user={user}
        appVersion={appVersion}
      />

      {/* Khu vực nội dung */}
      <div className="flex flex-col flex-1 min-w-0">
        {user && <Header user={user} />}
        <div className="flex-1 overflow-auto bg-gray-100 p-6">
          {children}

          <AdminMandatoryNotifications />
          <MaintenanceLockOverlay />
          <DesktopUnreadReminder />

         {/* FOOTER BOTTOM-RIGHT */}
         <div className="
            fixed bottom-2 right-4 
            text-xs text-gray-500 select-none
            bg-white/80 backdrop-blur-sm 
            px-3 py-1 rounded-md shadow-sm
          "
        >
          Powered by <span className="font-semibold">Hameno Software</span> • Version {appVersion?.slice(1,) || "0.0.0"}
        </div>

        </div>
      </div>
    </div>
  );
}
